import jsPDF from 'jspdf';
import i18n from '../i18n';
import { GlobalOrderData } from '../../hooks/useGlobalOrderData';
import { getTranslatedProductName } from '../utils/translationHelpers';

export interface PDFGenerationOptions {
  orderData: GlobalOrderData;
  orderId: string;
  customerEmail?: string;
  t?: (key: string, options?: any) => string; // Translation function
  includeItems?: boolean;
  includeCustomerInfo?: boolean;
}

export class PDFService {
  static async generateOrderPDF(options: PDFGenerationOptions): Promise<Blob> {
    const { orderData, orderId, t } = options;
    
    // Use provided translation function, or fall back to i18n instance
    // This ensures PDFs are generated in the current language (EN or FR)
    const translate = t || ((key: string, options?: any) => {
      return i18n.t(key, options || {});
    });
    
    // Get current language and date formatting options (used throughout PDF)
    const currentLanguage = i18n.language || 'en';
    const dateOptions: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    };
    const formatDate = (dateString?: string) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString(currentLanguage === 'fr' ? 'fr-CA' : 'en-US', dateOptions);
      } catch {
        return dateString;
      }
    };
    
    // Create new PDF document
    const doc = new jsPDF();
    
    // Set font
    doc.setFont('helvetica');
    
    // Add McKenzie logo
    try {
      // Load the logo image
      const logoResponse = await fetch('/Mckenzie-seed-logo.png');
      const logoBlob = await logoResponse.blob();
      const logoArrayBuffer = await logoBlob.arrayBuffer();
      const logoBase64 = btoa(String.fromCharCode(...new Uint8Array(logoArrayBuffer)));
      
      // Add logo to PDF (positioned at top left) - reduced size
      doc.addImage(logoBase64, 'PNG', 20, 10, 35, 17);
    } catch (error) {
      console.warn('Could not load logo, using fallback:', error);
      // Fallback: McKenzie text with green background
      doc.setFillColor(70, 130, 78); // McKenzie green color
      doc.rect(20, 15, 30, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('MCKENZIE', 25, 25);
    }
    
    // Company Information (top right) - reduced font size
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('McKenzie Seeds', 160, 15);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    const companyInfo = [
      '1000 Parker Blvd',
      'Brandon, MB R7A 6E1',
      'Eng/Fr 1-800-665-6340',
      'Fax 1-888-883-8222'
    ];
    
    companyInfo.forEach((line, index) => {
      doc.text(line, 160, 20 + (index * 3.5));
    });
    
    // Title - reduced size
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(translate('pdf.orderConfirmation'), 20, 42);
    
    // PO Number and Date - reduced spacing
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${translate('pdf.poNumber')}: ${orderData.poNumber || translate('pdf.na')}`, 20, 50);
    // Format date according to current language
    const formattedDate = formatDate(new Date().toISOString());
    doc.text(`${translate('pdf.date')}: ${formattedDate}`, 20, 56);
    
    // Draw a line under header
    doc.setDrawColor(70, 130, 78);
    doc.setLineWidth(0.5);
    doc.line(20, 62, 190, 62);
    
    // Customer Information Section - Customer-specific fields only
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(translate('pdf.customerInformation'), 20, 72);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    let yPos = 79;
    
    // Customer #
    doc.setFont('helvetica', 'bold');
    doc.text(`${translate('pdf.customerHash')}:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(orderData.customerNumber || translate('pdf.na'), 20 + 25, yPos);
    
    yPos += 4;
    
    // Address line: Address, City, Province, Postal Code
    const addressParts = [
      orderData.address || '',
      orderData.city || '',
      orderData.province || '',
      orderData.postalCode || ''
    ].filter(part => part).join(', ');
    
    doc.setFont('helvetica', 'bold');
    doc.text(`${translate('pdf.address')}:`, 20, yPos);
    doc.setFont('helvetica', 'normal');
    const addressLines = doc.splitTextToSize(addressParts || translate('pdf.na'), 120);
    doc.text(addressLines, 20 + 25, yPos);
    yPos += Math.max(4, addressLines.length * 4);
    
    // Phone
      doc.setFont('helvetica', 'bold');
    doc.text(`${translate('pdf.phone')}:`, 20, yPos);
      doc.setFont('helvetica', 'normal');
    doc.text(orderData.phoneNumber || translate('pdf.na'), 20 + 25, yPos);
    
    // Order Information Section (Right Column) - Order-specific fields
    let orderYPos = 79;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(translate('pdf.orderInformation'), 110, 72);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Store Name
    doc.setFont('helvetica', 'bold');
    doc.text(`${translate('pdf.storeName')}:`, 110, orderYPos);
    doc.setFont('helvetica', 'normal');
    doc.text(orderData.storeName || translate('pdf.na'), 110 + 25, orderYPos);
    
    orderYPos += 4;
    
    // Order Type
    const orderTypeText = orderData.orderType === 'custOrder' ? translate('pdf.custOrder') : 
                          orderData.orderType === 'offCarDelivery' ? translate('pdf.offCarDelivery') : 
                          translate('pdf.standardOrder');
    
    doc.setFont('helvetica', 'bold');
    doc.text(`${translate('pdf.orderType')}:`, 110, orderYPos);
    doc.setFont('helvetica', 'normal');
    doc.text(orderTypeText, 110 + 25, orderYPos);
    
    orderYPos += 4;
    
    // Special Instructions only if present
    if (orderData.specialInstructions && orderData.specialInstructions.trim() !== '') {
      doc.setFont('helvetica', 'bold');
      doc.text(`${translate('pdf.specialInstructions')}:`, 110, orderYPos);
      doc.setFont('helvetica', 'normal');
      const instructionLines = doc.splitTextToSize(orderData.specialInstructions, 80);
      doc.text(instructionLines, 110 + 35, orderYPos);
      orderYPos += Math.max(4, instructionLines.length * 4);
    }
    
    // Product Selections Section - reduced spacing, use max of both Y positions
    const productStartY = Math.max(95, Math.max(yPos, orderYPos) + 4);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(translate('pdf.productSelections'), 20, productStartY);
    
    // Draw table header - reduced height
    doc.setFillColor(240, 240, 240);
    doc.rect(20, productStartY + 3, 170, 6, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(translate('pdf.product'), 22, productStartY + 7);
    doc.text(translate('pdf.qty'), 120, productStartY + 7);
    doc.text(translate('pdf.price'), 140, productStartY + 7);
    doc.text(translate('pdf.total'), 160, productStartY + 7);
    
    let currentY = productStartY + 12;
    doc.setFont('helvetica', 'normal');
    
    // Separate items by form
    const heritageItems = orderData.heritageItems || [];
    const livingstonItems = orderData.livingstonItems || [];
    const flowerItems = orderData.flowerItems || [];
    const vegetableItems = orderData.vegetableItems || [];
    const supplyItems = orderData.supplyItems || [];

    const allItems = [
      ...heritageItems.map(item => ({ ...item, category: 'Heritage' })),
      ...livingstonItems.map(item => ({ ...item, category: 'Livingston' })),
      ...flowerItems.map(item => ({ ...item, category: 'Flower' })),
      ...vegetableItems.map(item => ({ ...item, category: 'Vegetable' })),
      ...supplyItems.map(item => ({ ...item, category: 'Supply' }))
    ];
    
    if (allItems.length === 0) {
      doc.text(translate('pdf.noProductsSelected'), 22, currentY);
    } else {
      // Process each form section separately
      const formSections = [
        { items: heritageItems, name: translate('categories.heritage'), category: 'H' },
        { items: livingstonItems, name: 'Livingston', category: 'L' },
        { items: flowerItems, name: translate('categories.flower'), category: 'F' },
        { items: vegetableItems, name: translate('categories.vegetable'), category: 'V' },
        { items: supplyItems, name: translate('categories.supply', 'Supplies'), category: 'S' }
      ];

      for (const section of formSections) {
        if (section.items.length === 0) continue;

        // Check if we need a new page - increased threshold to fit more on one page
        if (currentY > 220) {
          doc.addPage();
          currentY = 20;
        }

        // Add form section header - reduced spacing
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`${section.name} ${translate('pdf.products')}:`, 20, currentY);
        currentY += 6;

        // Add table header for this section - reduced height
        doc.setFillColor(240, 240, 240);
        doc.rect(20, currentY, 170, 6, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(translate('pdf.product'), 22, currentY + 4);
        doc.text(translate('pdf.qty'), 120, currentY + 4);
        doc.text(translate('pdf.price'), 140, currentY + 4);
        doc.text(translate('pdf.total'), 160, currentY + 4);
        currentY += 9;

        // Process items in this section
        doc.setFont('helvetica', 'normal');
        for (let i = 0; i < section.items.length; i++) {
          const item = section.items[i];
          
          // Check if we need a new page - increased threshold
          if (currentY > 250) {
            doc.addPage();
            currentY = 20;
            // Add continuation header
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.text(`${section.name} ${translate('pdf.products')} (${translate('pdf.continued')}):`, 20, currentY);
            currentY += 6;
            doc.setFont('helvetica', 'normal');
          }
          
          // Alternate row colors - reduced height
          if (i % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(20, currentY - 1.5, 170, 5, 'F');
          }
          
          // Product name with category badge
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.text(`[${section.category}]`, 22, currentY);
          doc.setFont('helvetica', 'normal');
          
          // Product name and code - translate product name, but keep code in English
          // Note: Product names are stored in English in database, but displayed translated
          const productName = item.packetDescription || `${section.name} Product`;
          const translatedProductName = getTranslatedProductName(productName, translate);
          const productCode = item.code ? ` (${translate('pdf.code')}: ${item.code})` : '';
          const fullProductText = translatedProductName + productCode;
          const productLines = doc.splitTextToSize(fullProductText, 85);
          doc.text(productLines, 30, currentY);
          
          // Quantity
          doc.text(item.quantity.toString(), 120, currentY);
          
          // Unit Price
          doc.text(`$${item.price.toFixed(2)}`, 140, currentY);
          
          // Total Price
          doc.text(`$${(item.price * item.quantity).toFixed(2)}`, 160, currentY);
          
          currentY += Math.max(5, productLines.length * 4.5); // Reduced spacing
        }

        // Add subtotal for this form - reduced spacing
        const sectionTotal = section.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const sectionItemCount = section.items.reduce((sum, item) => sum + item.quantity, 0);
        
        currentY += 3;
        doc.setDrawColor(70, 130, 78);
        doc.setLineWidth(0.5);
        doc.line(20, currentY, 190, currentY);
        currentY += 3;
        
        const itemLabel = sectionItemCount === 1 ? translate('pdf.item') : translate('pdf.items');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`${section.name} ${translate('pdf.subtotal')} (${sectionItemCount} ${itemLabel}): $${sectionTotal.toFixed(2)}`, 20, currentY);
        currentY += 7;
      }
    }
    
    // Total Section (always on last page) - reduced spacing
    if (currentY > 250) {
      doc.addPage();
      currentY = 20;
    }
    
    currentY += 5;
    doc.setDrawColor(70, 130, 78);
    doc.setLineWidth(1);
    doc.line(20, currentY, 190, currentY);
    
    currentY += 5;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    
    const totalItems = allItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = allItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    const totalItemLabel = totalItems === 1 ? translate('pdf.item') : translate('pdf.items');
    doc.text(`${translate('pdf.total').toUpperCase()} (${totalItems} ${totalItemLabel}): $${totalAmount.toFixed(2)}`, 20, currentY);
    
    // Signature Section - reduced spacing and size
    // Check if we need a new page (leave at least 50mm for signature section and footer)
    const pageHeight = 297; // A4 height in mm
    const requiredSpace = 50; // Reduced from 60mm
    
    if (currentY + requiredSpace > pageHeight - 15) {
      doc.addPage();
      currentY = 20; // Reset Y position for new page
    }
    
    currentY += 8; // Reduced from 15
    
    // Draw signature box for customer only (removed sales rep signature)
    doc.setDrawColor(70, 130, 78);
    doc.setLineWidth(1);
    
    // Customer Signature Box (Centered, smaller size) - further reduced
    const signatureBoxWidth = 90; // Reduced from 100mm
    const signatureBoxHeight = 30; // Reduced from 35mm
    const signatureBoxX = (210 - signatureBoxWidth) / 2; // Center on page (A4 width is 210mm)
    
    doc.rect(signatureBoxX, currentY, signatureBoxWidth, signatureBoxHeight);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(translate('pdf.customer'), signatureBoxX + 3, currentY - 3);
    
    // Add customer signature image if available
    if (orderData.customerSignature) {
      try {
        // Add signature image to PDF (smaller size to fit in the box)
        // The signature is stored as a base64 data URL
        const signatureData = orderData.customerSignature;
        // Use smaller dimensions with padding
        const imageWidth = signatureBoxWidth - 8; // 82mm
        const imageHeight = signatureBoxHeight - 8; // 22mm
        doc.addImage(signatureData, 'PNG', signatureBoxX + 4, currentY + 4, imageWidth, imageHeight);
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        // If signature fails to load, just show the box
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(translate('pdf.nameAndSignature'), signatureBoxX + 3, currentY + signatureBoxHeight + 5);
      }
    } else {
      // No signature provided, show placeholder text
    doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(translate('pdf.nameAndSignature'), signatureBoxX + 3, currentY + signatureBoxHeight + 5);
    }
    
    // Footer - reduced spacing
    currentY += signatureBoxHeight + 8; // Reduced from 15
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(translate('pdf.thankYou'), 20, currentY);
    doc.text(translate('pdf.mckenziePortalTeam'), 20, currentY + 5);
    
    // Convert to blob
    const pdfBlob = doc.output('blob');
    return pdfBlob;
  }
  
  static downloadPDF(pdfBlob: Blob, filename: string): void {
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  
  static previewPDF(pdfBlob: Blob): void {
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
    // Clean up the URL after a delay to allow the browser to load it
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
